import { useQuery } from '@tanstack/react-query';
import { Badge } from '../atoms';
import { api } from '../lib/api';
import { GenericListByConfig } from './GenericList';
import { LEVEL_LABEL, type RecipientLevel } from '../lib/recipientLevel';

/**
 * The operations Contacts list.
 *
 * Contacts are external people (client reps, assessor/collector reps, …), a
 * separate concern from internal users — the crud router already excludes
 * internal-user rows — so the list shows contact identity + company without the
 * internal `type` column (and its filter).
 *
 * Part 4 §4.4: every row states the LEVEL it sits at, because level drives who
 * receives what — it is not decoration. Client-side contacts read Client/Property/
 * Parcel level; government contacts read their SOURCE (assessor / collector /
 * jurisdiction), which is what they carry instead of a client (Y11). The level comes
 * from the same contact_list link the profile Contacts tabs read, so the two surfaces
 * cannot disagree.
 *
 * The CLIENT column resolves `client_id` → name in the browser, off the same cached
 * `clients-list` query ContactDetail uses, rather than joining in the API. Adding a
 * `FROM clients` subquery to the generic crud router would classify that file as a
 * sensitive-table read surface (read-endpoint-rowscope-coverage.test.js), and that
 * guard asks each such file for ONE scoping story — which a router serving every
 * table cannot honestly give.
 */

// Government contacts carry a source rather than a level. Rendered in the same
// column because the question a reader is asking ("what is this contact attached
// to?") is the same one; only the answer's vocabulary differs.
const SOURCE_LABEL: Record<string, string> = {
  assessor: 'Assessor',
  collector: 'Collector',
  jurisdiction: 'Jurisdiction',
};

export function ContactsListPage() {
  // Same queryKey as ContactDetail's picker, so the two share one cached fetch.
  const clientsQuery = useQuery<{ id: string; name: string }[]>({
    queryKey: ['clients-list'],
    queryFn: ({ signal }) => api.get<{ id: string; name: string }[]>('/api/clients', signal),
    staleTime: 5 * 60 * 1000,
  });
  const clientNameById = new Map((clientsQuery.data ?? []).map((c) => [c.id, c.name] as const));

  return (
    <GenericListByConfig
      table="contacts"
      title="Contacts"
      columns={['first_name', 'last_name', 'company_name']}
      derivedColumns={[
        {
          name: 'contact_level',
          label: 'Level',
          render: (value) => {
            if (!value) return '—';
            const key = String(value);
            const clientSide = LEVEL_LABEL[key as RecipientLevel];
            if (clientSide) return <Badge variant="neutral">{clientSide}</Badge>;
            return <Badge variant="info">{SOURCE_LABEL[key] ?? key}</Badge>;
          },
        },
        {
          name: 'client_id',
          label: 'Client',
          // A government contact legitimately has none (Y11) and reads '—'. While the
          // client list is still loading an id would render as a raw uuid, so hold the
          // dash until it resolves.
          render: (value) => {
            if (!value) return '—';
            return clientNameById.get(String(value)) ?? '—';
          },
        },
      ]}
    />
  );
}
